import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  AppBar,
  Toolbar,
  Link as MuiLink,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  Search,
  AutoAwesome,
  Create
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SubscriptionManager from './subscription/SubscriptionManager';

/**
 * LandingPage Component
 * Public marketing page for Wyspur AI
 * Features: Hero, Video, 3-Step Process, Pricing
 */
const LandingPage = () => {
  const navigate = useNavigate();

  // Add rb2b tracking script and Manrope font to head
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `!function(key) {if (window.reb2b) return;window.reb2b = {loaded: true};var s = document.createElement("script");s.async = true;s.src = "https://ddwl4m2hdecbv.cloudfront.net/b/" + key + "/" + key + ".js.gz";document.getElementsByTagName("script")[0].parentNode.insertBefore(s, document.getElementsByTagName("script")[0]);}("0OV0VHM7ZY6Z");`;
    document.head.appendChild(script);

    // Add Manrope font
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    return () => {
      // Cleanup: remove script when component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (fontLink.parentNode) {
        fontLink.parentNode.removeChild(fontLink);
      }
    };
  }, []);

  // Wyspur brand colors
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#0095FF',
    lightBlue: '#40C4FF',
    purple: '#9C27B0',
    yellow: '#faa32c',
    pink: '#E83E8C',
    text: '#FFFFFF',
    border: '#3C3C5A',
    subtitle: '#FFFFFF'
  };

  // Smooth scroll to pricing section
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Smooth scroll to curate section
  const scrollToCurate = () => {
    const curateSection = document.getElementById('curate-section');
    if (curateSection) {
      curateSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box sx={{ bgcolor: wyspurColors.darkNavy, minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: wyspurColors.darkNavy,
          borderBottom: `1px solid ${wyspurColors.border}`
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 1,
            px: { xs: 2, sm: 4, md: 6, lg: 8 }  // Responsive horizontal padding
          }}
        >
          {/* Left Side: Logo + Features/Pricing/Help */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
            {/* Logo */}
            <Box
              component="img"
              src="/logo-full-cropped.png"
              alt="Wyspur AI"
              sx={{
                height: { xs: '40px', md: '50px' },  // Smaller on mobile
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            {/* Left Navigation Links: Features, Pricing, Help */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3, mt: 1.5 }}>
              <MuiLink
                component="button"
                onClick={scrollToCurate}
                sx={{
                  color: wyspurColors.text,
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
              >
                Features
              </MuiLink>
              <MuiLink
                component="button"
                onClick={scrollToPricing}
                sx={{
                  color: wyspurColors.text,
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
              >
                Pricing
              </MuiLink>
              <MuiLink
                href="https://intercom.help/wyspur-ai/en/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: wyspurColors.text,
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
              >
                Help
              </MuiLink>
            </Box>
          </Box>

          {/* Right Side: Login + Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
            <MuiLink
              component="button"
              onClick={() => navigate('/login')}
              sx={{
                display: { xs: 'none', md: 'inline' },  // Hidden on mobile
                color: wyspurColors.text,
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': { color: wyspurColors.blue }
              }}
            >
              Login
            </MuiLink>
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
              sx={{
                bgcolor: wyspurColors.blue,
                color: wyspurColors.text,
                fontWeight: 600,
                px: { xs: 2, md: 3 },  // Less padding on mobile
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.875rem', md: '1rem' },  // Smaller text on mobile
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { bgcolor: '#0077CC' }
              }}
            >
              {/* Shorter text on mobile */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                START FOR FREE
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                START FREE
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero and Video Section with lighter background */}
      <Box sx={{ bgcolor: 'rgba(22, 28, 62, 0.5)', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 }
            }}
          >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              color: '#20A4FF',
              mb: 3,
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.2,
              px: { xs: 1, sm: 0 },
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}
          >
            Your Outbound Sales Assistant
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: wyspurColors.text,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 600,
              px: { xs: 2, sm: 4, md: 0 },
              lineHeight: 1.2,
              textAlign: 'center'
            }}
          >
            Clean and Enrich Prospect Lists
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: wyspurColors.text,
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 400,
              px: { xs: 2, sm: 4, md: 8 },
              lineHeight: 1.6,
              textAlign: 'center',
              opacity: 0.9
            }}
          >
            Fix messy lists for LinkedIn or email automation campaigns, analyze<br />
            prospect and company descriptions, and research websites with AI
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              bgcolor: wyspurColors.blue,
              color: wyspurColors.text,
              fontWeight: 600,
              px: { xs: 3, md: 5 },
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              borderRadius: 2,
              textTransform: 'uppercase',
              '&:hover': { bgcolor: '#0077CC' }
            }}
          >
            Start for Free
          </Button>
        </Box>

        {/* Video Section */}
        <Box sx={{ mb: { xs: 6, md: 8 }, px: { xs: 1, sm: 2, md: 0 } }}>
          {/* YouTube Embed */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '600px', md: '800px' },
              mx: 'auto',
              aspectRatio: '16/9',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/f3836JR58lI"
              title="Wyspur AI Platform Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        </Box>
        </Container>
      </Box>

      {/* White Background Section for Curate + Steps - Full Width */}
      <Box id="curate-section" sx={{
        bgcolor: '#F0F7FF',
        py: { xs: 6, md: 8 }
      }}>
        <Container maxWidth="lg">
            {/* Curate Heading */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: wyspurColors.darkNavy,
                textAlign: 'center',
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
              }}
            >
              Curate Your Perfect List Today
            </Typography>

            {/* 3-Step Process Section - FLEX LAYOUT */}
            <Box sx={{ mb: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 4, md: 3 },
              maxWidth: '1200px',
              mx: 'auto'
            }}
          >
            {/* Step 1 */}
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: '350px',
                width: '100%',
                px: 2
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: `0 4px 20px rgba(0, 149, 255, 0.4)`
                }}
              >
                <Typography variant="h3" sx={{ color: wyspurColors.text, fontWeight: 700 }}>
                  1
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ color: wyspurColors.darkNavy, fontWeight: 600, mb: 2 }}
              >
                Upload Your Prospect List
              </Typography>
              <Box sx={{ minHeight: '60px', mb: 3 }}>
                <Typography variant="body1" sx={{ color: wyspurColors.darkNavy, opacity: 0.7 }}>
                  Import a CSV from Heyreach, Apollo, Instantly, your CRM or another source
                </Typography>
              </Box>
              <Box
                component="img"
                src="/csv-preview.png"
                alt="CSV Preview"
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 149, 255, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  mt: 10
                }}
              />
            </Box>

            {/* Step 2 */}
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: '350px',
                width: '100%',
                px: 2
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: `0 4px 20px rgba(0, 149, 255, 0.4)`
                }}
              >
                <Typography variant="h3" sx={{ color: wyspurColors.text, fontWeight: 700 }}>
                  2
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ color: wyspurColors.darkNavy, fontWeight: 600, mb: 2 }}
              >
                Define Your Qualification Criteria
              </Typography>
              <Box sx={{ minHeight: '60px', mb: 3 }}>
                <Typography variant="body1" sx={{ color: wyspurColors.darkNavy, opacity: 0.7 }}>
                  Give Wyspur AI instructions about how to qualify or enrich your leads
                </Typography>
              </Box>
              <Box
                component="img"
                src="/filter-criteria.png"
                alt="Filter Criteria"
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 149, 255, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
              />
            </Box>

            {/* Step 3 */}
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: '350px',
                width: '100%',
                px: 2
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: `0 4px 20px rgba(0, 149, 255, 0.4)`
                }}
              >
                <Typography variant="h3" sx={{ color: wyspurColors.text, fontWeight: 700 }}>
                  3
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ color: wyspurColors.darkNavy, fontWeight: 600, mb: 2 }}
              >
                Review & Export
              </Typography>
              <Box sx={{ minHeight: '60px', mb: 3 }}>
                <Typography variant="body1" sx={{ color: wyspurColors.darkNavy, opacity: 0.7 }}>
                  Quickly verify with our human-in-the-loop system and export your new list
                </Typography>
              </Box>
              <Box
                component="img"
                src="/review.png"
                alt="Review and Export"
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 149, 255, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  mt: 6
                }}
              />
            </Box>
          </Box>
          </Box>
        </Container>
      </Box>

      {/* Research Websites Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: wyspurColors.blue }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Heading */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: wyspurColors.text,
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
            }}
          >
            Research Websites
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: wyspurColors.text,
              textAlign: 'center',
              mb: { xs: 6, md: 8 },
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 700,
              textTransform: 'uppercase',
              fontFamily: 'Manrope, sans-serif',
              lineHeight: 1.2,
              letterSpacing: '0.15em'
            }}
          >
            Verify business details and extract information at scale
          </Typography>

          {/* Problem-Solution Boxes */}
          <Stack spacing={3}>
            {/* Box 1: Looking to find niche industries */}
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                border: `1px solid rgba(0, 0, 0, 0.1)`,
                borderRadius: 2,
                p: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CheckCircle sx={{ color: '#0095FF', fontSize: '1.5rem' }} />
                <Typography
                  sx={{
                    color: '#000000',
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Looking to find niche industries?
                </Typography>
              </Box>
              <Box sx={{ borderTop: `1px solid rgba(0, 0, 0, 0.1)`, pt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Search sx={{ color: '#faa32c', fontSize: '1.5rem' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#000000',
                      fontSize: { xs: '1.125rem', md: '1.25rem' },
                      fontWeight: 600,
                      fontFamily: 'Manrope, sans-serif'
                    }}
                  >
                    AI-powered website analysis and review
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#333333',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.7,
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Prompt: Go to each prospect's website and verify that they work for a Manufacturer that makes toys for children under 12 years old.
                </Typography>
              </Box>
            </Box>

            {/* Box 2: Need to extract specific information */}
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                border: `1px solid rgba(0, 0, 0, 0.1)`,
                borderRadius: 2,
                p: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CheckCircle sx={{ color: '#0095FF', fontSize: '1.5rem' }} />
                <Typography
                  sx={{
                    color: '#000000',
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Need to extract specific information?
                </Typography>
              </Box>
              <Box sx={{ borderTop: `1px solid rgba(0, 0, 0, 0.1)`, pt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AutoAwesome sx={{ color: '#faa32c', fontSize: '1.5rem' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#000000',
                      fontSize: { xs: '1.125rem', md: '1.25rem' },
                      fontWeight: 600,
                      fontFamily: 'Manrope, sans-serif'
                    }}
                  >
                    Automate your manual research
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#333333',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.7,
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Prompt: Review each company website and tell me which industries they work with.
                </Typography>
              </Box>
            </Box>

            {/* Box 3: Personalized message */}
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                border: `1px solid rgba(0, 0, 0, 0.1)`,
                borderRadius: 2,
                p: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CheckCircle sx={{ color: '#0095FF', fontSize: '1.5rem' }} />
                <Typography
                  sx={{
                    color: '#000000',
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Want a personalized message from your research?
                </Typography>
              </Box>
              <Box sx={{ borderTop: `1px solid rgba(0, 0, 0, 0.1)`, pt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Create sx={{ color: '#faa32c', fontSize: '1.5rem' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#000000',
                      fontSize: { xs: '1.125rem', md: '1.25rem' },
                      fontWeight: 600,
                      fontFamily: 'Manrope, sans-serif'
                    }}
                  >
                    Generate a unique note with context
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#333333',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.7,
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Prompt: Pitch my services to each company in less than 50 words, making sure to mention specific goals on their website and how our offer can support them in achieving them.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing-section" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'rgba(22, 28, 62, 0.5)' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: wyspurColors.text,
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: wyspurColors.text,
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              opacity: 0.8,
              fontSize: { xs: '1rem', md: '1.25rem' },
              px: { xs: 2, md: 0 }
            }}
          >
            Select the right plan for your team.
          </Typography>
          <SubscriptionManager isOnboarding={true} />
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: wyspurColors.darkNavy,
          py: { xs: 3, md: 4 },
          borderTop: `1px solid ${wyspurColors.border}`
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Footer content - Logo left, Product/Legal right */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 4, md: 8 }
            }}
          >
            {/* Logo on the left */}
            <Box
              component="img"
              src="/logo-full-cropped.png"
              alt="Wyspur AI"
              sx={{
                height: '40px',
                width: 'auto'
              }}
            />

            {/* Product and Legal sections on the right, aligned horizontally */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 4, md: 8 },
                alignItems: 'flex-start'
              }}
            >
              {/* Product Section */}
              <Box sx={{ minWidth: '120px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: wyspurColors.text,
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  Product
                </Typography>
                <Stack spacing={1}>
                  <MuiLink
                    component="button"
                    onClick={scrollToPricing}
                    sx={{
                      color: wyspurColors.text,
                      opacity: 0.7,
                      textDecoration: 'none',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    Pricing
                  </MuiLink>
                  <MuiLink
                    component="button"
                    onClick={() => navigate('/signup')}
                    sx={{
                      color: wyspurColors.text,
                      opacity: 0.7,
                      textDecoration: 'none',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    Sign Up
                  </MuiLink>
                  <MuiLink
                    component="button"
                    onClick={() => navigate('/login')}
                    sx={{
                      color: wyspurColors.text,
                      opacity: 0.7,
                      textDecoration: 'none',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    Login
                  </MuiLink>
                </Stack>
              </Box>

              {/* Legal Section */}
              <Box sx={{ minWidth: '120px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: wyspurColors.text,
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  Legal
                </Typography>
                <Stack spacing={1}>
                  <MuiLink
                    component="button"
                    onClick={() => navigate('/terms')}
                    sx={{
                      color: wyspurColors.text,
                      opacity: 0.7,
                      textDecoration: 'none',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    Terms of Service
                  </MuiLink>
                  <MuiLink
                    component="button"
                    onClick={() => navigate('/privacy')}
                    sx={{
                      color: wyspurColors.text,
                      opacity: 0.7,
                      textDecoration: 'none',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    Privacy Policy
                  </MuiLink>
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Copyright */}
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${wyspurColors.border}` }}>
            <Typography
              variant="body2"
              sx={{
                color: wyspurColors.text,
                opacity: 0.6,
                textAlign: 'center',
                fontSize: '0.875rem'
              }}
            >
              © {new Date().getFullYear()} Wyspur AI. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
